import {
  IconBuildingCommunity,
  IconBuildingWarehouse,
  IconChartBar,
  IconCurrencyDollar,
  IconFileImport,
  IconFilter,
  IconHelp,
  IconLayoutDashboard,
  IconPackage,
  IconPhoto,
  IconServerCog,
  IconSettings2,
  IconShieldLock,
  IconShoppingCart,
  IconSparkles,
  IconTag,
  IconTimeline,
  IconTrendingUp,
  IconTruck,
  IconUsers,
} from '@tabler/icons-react'
import type * as React from 'react'
import { useTranslation } from 'react-i18next'
import logoDark from '@/assets/logo-dark.png'
import { SidebarNav } from '@/domains/shared/components/SidebarNav'
import { UserBar } from '@/domains/shared/components/UserBar'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/domains/shared/components/ui/Sidebar'
import { APP_NAME } from '@/domains/shared/constants'
import { useIsSuperAdmin, usePermissionCheck } from '@/domains/shared/hooks/useHasPermission'
import type { Action } from '@/domains/shared/permissions/actions'
import { useAuthStore } from '@/store/useAuthStore'

interface NavEntry {
  title: string
  url: string
  icon: React.ElementType
  external?: boolean
  /** The action(s) the entry needs; an entry without one is open to any signed-in user. */
  action?: Action | readonly Action[]
  /** Behind the SuperAdmin grant rather than an action (the Companies page). */
  superAdmin?: true
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const user = useAuthStore((s) => s.user)
  const can = usePermissionCheck()
  const isSuperAdmin = useIsSuperAdmin()
  const { t } = useTranslation('translation', { keyPrefix: 'component.sidebar.nav' })

  // Every entry declares the action of the route it opens (the same one the route
  // guard in App.tsx checks), so the sidebar never offers a page that would render
  // the Forbidden page.
  const allGroups: { label: string; items: NavEntry[] }[] = [
    {
      label: t('overview'),
      items: [
        {
          title: t('dashboard'),
          url: '/pulse',
          icon: IconLayoutDashboard,
          action: 'dashboard.view',
        },
      ],
    },
    {
      label: t('catalog'),
      items: [
        {
          title: t('products'),
          url: '/products',
          icon: IconPackage,
          action: 'products.view',
        },
        {
          title: t('categories'),
          url: '/categories',
          icon: IconTag,
          action: 'categories.view',
        },
        {
          title: t('suppliers'),
          url: '/suppliers',
          icon: IconTruck,
          action: 'suppliers.view',
        },
        {
          title: t('imports'),
          url: '/imports',
          icon: IconFileImport,
          // The import creates products, so it needs product creation too.
          action: ['suppliers.import', 'products.create'],
        },
        {
          title: t('media'),
          url: '/media',
          icon: IconPhoto,
          action: 'media.view',
        },
        {
          title: t('warehouse'),
          url: '/warehouses',
          icon: IconBuildingWarehouse,
          action: 'warehouses.view',
        },
      ],
    },
    {
      label: t('sales'),
      items: [
        {
          title: t('channels'),
          url: '/channels',
          icon: IconShoppingCart,
          action: 'channels.view',
        },
        {
          title: t('rules'),
          url: '/rules',
          icon: IconFilter,
          action: 'channel_rules.view',
        },
        {
          title: t('pricing'),
          url: '/pricing',
          icon: IconCurrencyDollar,
          action: 'pricing_rules.view',
        },
        {
          title: t('competitorPrices'),
          url: '/competitor-prices',
          icon: IconTrendingUp,
          action: ['competitor_prices.view', 'prices.view'],
        },
        {
          title: t('campaigns'),
          url: '/campaigns',
          icon: IconChartBar,
          action: 'campaigns.view',
        },
      ],
    },
    {
      label: t('ai'),
      items: [
        {
          title: t('bulkRuns'),
          url: '/ai/bulk-runs',
          icon: IconSparkles,
          action: 'ai.bulk_runs',
        },
      ],
    },
    {
      label: t('system'),
      items: [
        {
          title: t('queueMonitoring'),
          url: `${import.meta.env.VITE_API_URL}/admin/bull`,
          icon: IconServerCog,
          external: true,
          // The board spans every company's queues: the grant, not a Role.
          superAdmin: true,
        },
        {
          title: t('grafana'),
          url: 'https://grafana.inovio.rs',
          icon: IconTimeline,
          external: true,
          action: 'system.jobs',
        },
        {
          title: t('help'),
          url: '/help',
          icon: IconHelp,
        },
      ],
    },
    {
      label: t('settings'),
      items: [
        {
          title: t('general'),
          url: '/settings',
          icon: IconSettings2,
        },
        {
          title: t('users'),
          url: '/settings/users',
          icon: IconUsers,
          action: 'users.view',
        },
        {
          title: t('roles'),
          url: '/settings/roles',
          icon: IconShieldLock,
          action: 'roles.view',
        },
        {
          title: t('companies'),
          url: '/settings/companies',
          icon: IconBuildingCommunity,
          superAdmin: true,
        },
      ],
    },
  ]

  const navGroups = allGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.superAdmin ? isSuperAdmin : item.action === undefined || can(item.action),
      ),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2.5 py-3.5">
          <img
            src={logoDark}
            alt={APP_NAME}
            className="h-5 shrink-0 group-data-[collapsible=icon]:hidden"
          />
          <img
            src="/favicon.svg"
            alt={APP_NAME}
            className="hidden size-5 shrink-0 rounded group-data-[collapsible=icon]:block"
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarNav groups={navGroups} />
      </SidebarContent>
      <SidebarFooter>
        <UserBar
          user={{
            name: user?.name ?? '',
            email: user?.email ?? '',
            avatar: '',
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
